import { getFeatureConfig } from '@crossframe/apis/FeatureConfig'
import { getDeviceDetails } from '@services/CandyCaneService'
import {
    getDevicePlatform,
    getDomain,
    getRegion,
    getStoreFamily,
    getStoreId,
    isAndroid,
} from '@utils/EnvironmentUtil'
import _ from 'lodash'

const supportedAttributes = {
    DEVICE_ID: 'deviceid',
    STORE_ID: 'storeid',
    DOMAIN: 'domain',
    REGION: 'region',
    PLATFORM: 'platform',
    STORE_FAMILY: 'storefamily',
}

const enum SupportedOperators {
    EQUALS = 'equals',
    NOT_EQUALS = 'notequals',
    STARTS_WITH = 'startswith',
    CONTAINS = 'contains',
}

export const enum featureConfigFeatures {
    ENABLE_WFM_STORES_NOTIFICATIONS = 'enable_wfm_stores_notifications',
    EXTERNAL_USER_LOGIN_FEATURE = 'external_user_login_feature',
    VERDE_STORE_TYPE_FEATURE = 'verde_store_type_feature',
    KELPIE_SERVICE_IS_FEATURE_ENABLED = 'kelpie_service_is_feature_enabled_feature',
    ENABLE_INVENTORY_VIEWER_MODAL = 'enable_inventory_viewer_modal',
    ENABLE_MASTER_INDIRECT_TASK = 'enable_master_indirect_task_selection',
    ENABLE_RIVER_FEATURE = 'enable_river_modal',
    ENABLE_IIB = 'enable_ihm_identity_broker',
    ENABLE_GEN_AI_ASSISTANT = 'enable_gen_ai_assistant',
    ENABLE_ACCOUNT_SETUP_GATE = 'golden_gate_account_setup_gate',
}

let features: any
let dsn: any
let environmentAttributes: any
let loadPromise: Promise<any>

export const validateAndSortAttributes = (attributes) => {
    validateAttributes(attributes)
    return attributes.sort()
}

export const isValidAttribute = (attribute) => {
    return Object.values(supportedAttributes).indexOf(attribute.toLowerCase()) !== -1
}

export const isUndefinedOrNull = (value) => {
    return _.isUndefined(value) || _.isNull(value)
}

export const getEnvironmentAttribute = (attribute, environmentAttribute) => {
    return environmentAttribute[attribute.toLowerCase()]
}

export const forceReload = () => {
    return loadFeatures()
}

export const load = () => {
    if (loadPromise) {
        return loadPromise
    }

    return loadFeatures()
}

export const loadFeatures = () => {
    console.debug('Loading features')

    const fileLoadPromise = getFeatureConfig()
        .then((result) => {
            features = result
            console.log('ATBase: Feature list: ', features)
        })
        .catch((error) => {
            console.error('ATBase: FeatureConfigService: Error while loading the feature from crossframe call', error)
            return Promise.reject(error)
        })

    const dsnPromise =
        !isAndroid() ? Promise.resolve(undefined)
            : getDeviceDetails().then((result) => {
                dsn = result.deviceSerialNumber
            }).catch((error) => {
                console.error('ATBase: FeatureConfigService: Error while getting device serial number', error)
            })

    const pendingAttributes = {}
    pendingAttributes[supportedAttributes.REGION] = getRegion() || 'pdx'
    const storeId = getStoreId()
    if (storeId) {
        pendingAttributes[supportedAttributes.STORE_ID] = storeId
    }
    pendingAttributes[supportedAttributes.DOMAIN] = getDomain()
    pendingAttributes[supportedAttributes.PLATFORM] = getDevicePlatform()
    const storeFamily = getStoreFamily()
    if (storeFamily) {
        pendingAttributes[supportedAttributes.STORE_FAMILY] = storeFamily
    }

    loadPromise = fileLoadPromise
        .then(() => {
            dsnPromise
                .finally(() => {
                    pendingAttributes[supportedAttributes.DEVICE_ID] = dsn
                    environmentAttributes = Object.freeze(pendingAttributes)
                })
        })
        .catch((error) => {
            console.error('ATBase: FeatureConfigService: Could not load the FeatureConfigService', error)
            loadPromise = null
            return Promise.reject(error)
        })

    return loadPromise
}

export const isFeatureEnabled = (featureName, defaultValue) => {
    return evaluateFeature(featureName, defaultValue).featureEnabled
}

export const evaluateFeature = (featureName, defaultValue) => {
    console.log(`Determining if feature ${featureName} is enabled`)
    const result = evaluateFeatureInner(featureName, !!defaultValue)
    console.log('ATBase: Returning result for feature', featureName, ', result', result)
    return result
}

export const evaluateFeatureInner = (featureName, defaultValue) => {
    const result = {
        featureEnabled: defaultValue,
        overriden: false,
        treatmentApplied: false,
        error: null,
    }

    try {
        if (!featureName) {
            console.warn('No feature name provided')
            result.featureEnabled = defaultValue
            result.error = 'No feature name provided'
            return result
        }

        if (!features) {
            console.warn('Data is not loaded. returning default of ', defaultValue, 'for feature', featureName)
            result.featureEnabled = defaultValue
            result.error = 'Data is not loaded'
            return result
        }

        if (!features[featureName]) {
            console.warn('"' + featureName + '" is not in loaded data. Returning default of', defaultValue)
            result.featureEnabled = defaultValue
            result.error = 'Feature not in loaded data'
            return result
        }

        const feature = features[featureName]
        console.log('ATBase: Evaluating feature', feature, 'against environmentAttributes', environmentAttributes)

        let overridenResult
        try {
            overridenResult = processOverrides(feature.overrides)
        } catch (error) {
            console.warn(
                'Got error processing overrides. Returning default of',
                defaultValue,
                'for feature',
                featureName,
            )
            result.featureEnabled = defaultValue
            result.error = 'Invalid overrides or incomplete environmentAttributes'
            return result
        }

        if (overridenResult.overriden) {
            result.featureEnabled = overridenResult.featureEnabled
            result.overriden = true
            return result
        }

        console.log('ATBase: No overrides found for feature', featureName)

        if (!feature.treatment) {
            console.warn('No treatment found for feature. Returning default of', defaultValue, 'for feature', feature)
            result.featureEnabled = defaultValue
            return result
        }

        const treatment = feature.treatment
        const treatmentResult = processTreatment(treatment)

        if (!treatmentResult.treatmentApplied) {
            console.warn('Treatment not applied')
            result.featureEnabled = defaultValue
            result.error = treatmentResult.error
            return result
        }

        console.log('ATBase: Returning featureEnabled = ', treatmentResult.featureEnabled)

        result.treatmentApplied = true
        result.featureEnabled = treatmentResult.featureEnabled
        return result
    } catch (error) {
        console.error('ATBase: Got an error while determining the feature config', error)
        result.error = 'Unhandled error'
        result.featureEnabled = defaultValue
        return result
    }
}

export const processTreatment = (treatment) => {
    const result = {
        treatmentApplied: false,
        featureEnabled: null,
        error: null,
    }

    const percentEnabled = treatment.percentEnabled
    if (isUndefinedOrNull(percentEnabled) || percentEnabled < 0 || percentEnabled > 100) {
        console.debug('percentEnabled was invalid. Not applying treatment', treatment)
        result.error = 'Treatment percent was invalid'
        return result
    }

    if (percentEnabled === 100) {
        console.debug('percentEnabled is 100. Automatically, enabling feature', treatment)
        result.treatmentApplied = true
        result.featureEnabled = true
        return result
    }

    if (percentEnabled === 0) {
        console.debug('percentEnabled is 0. Automatically, disabling feature', treatment)
        result.treatmentApplied = true
        result.featureEnabled = false
        return result
    }

    if (!treatment.basedOn) {
        console.warn('no basedOn attributes on treatment. Not applying treatment', treatment)
        result.error = 'basedOn property missing on treatment'
        return result
    }

    let attributes
    try {
        attributes = validateAndSortAttributes(treatment.basedOn)
    } catch (error) {
        console.error('ATBase: Invalid treatment', treatment, 'error: ', error)
        result.error = 'Invalid treatment'
        return result
    }

    if (!attributes.length) {
        console.warn('No valid attributes found. Not applying treatment', treatment)
        result.error = 'No valid attributes found for treatment'
        return result
    }

    const undefinedOrNullAttributes = attributes
        .filter((attribute) => {
            return isUndefinedOrNull(getEnvironmentAttribute(attribute, environmentAttributes))
        })

    if (undefinedOrNullAttributes.length) {
        console.error('ATBase: Undefined or null attributes', undefinedOrNullAttributes)
        result.error = 'Undefined or null attributes: ' + undefinedOrNullAttributes.join(', ')
        return result
    }

    const stringToHash = attributes
        .map((attribute) => {
            const lowerCaseAttribute = attribute.toLowerCase()
            const value = '' + getEnvironmentAttribute(attribute, environmentAttributes) // make sure it's a string
            return lowerCaseAttribute + ':' + value
        })
        .join('|')

    console.debug('String to hash "' + stringToHash + '"')
    const hash = Math.abs(hashCode(stringToHash))
    console.debug('Hashed value', hash)

    const modResult = (hash % 100) + 1
    console.debug('Mod value', modResult, ', percentEnabled value', treatment.percentEnabled)

    const featureEnabled = modResult < treatment.percentEnabled

    result.treatmentApplied = true
    result.featureEnabled = featureEnabled
    return result
}

export const processOverrides = (overrides) => {
    if (!overrides) {
        return {
            overriden: false,
        }
    }

    const overrideMatchIndex = _.findIndex(overrides, isFeatureOverrideEnabled)
    if (overrideMatchIndex === -1) {
        return {
            overriden: false,
        }
    }

    const overrideMatch = overrides[overrideMatchIndex]
    console.log('ATBase: Found an override match', overrideMatch)

    return {
        overriden: true,
        featureEnabled: !!overrideMatch.featureEnabled,
    }
}

export const isFeatureOverrideEnabled = (override: object & { filter }) => {
    if (!_.isObject(override)) {
        console.error('ATBase: Invalid override', override)
        throw new Error('Invalid override')
    }

    const filter = override.filter
    let attributes

    try {
        attributes = validateAndSortAttributes(Object.keys(filter))
    } catch (error) {
        console.error('ATBase: Got error with override', override, 'error:', error)
        throw new Error('Invalid override: ' + error)
    }

    const invalidAttributes = []
    const overrideResult = _.every(attributes, (attribute) => {
        const filterOperation = filter[attribute]
        const operator = filterOperation.operator
        const filterValue = filterOperation.value

        if (isUndefinedOrNull(operator)) {
            console.error('ATBase: Invalid operator', operator, 'for attribute', attribute)
            throw new Error('Invalid operator ' + operator)
        }

        const environmentValue = getEnvironmentAttribute(attribute, environmentAttributes)
        if (isUndefinedOrNull(environmentValue)) {
            console.warn('environment value for', attribute, 'is', environmentValue)
            invalidAttributes.push(attribute)

            // We're returning true here to see what the outcome would look like without this attribute taken
            // into account. If this whole _.all returns true, then we will fail, but if it returns false, it
            // means that it would have been false regardless of the value of this attribute.
            return true
        }

        return evaluateOperation(operator, filterValue, environmentValue)
    })

    if (overrideResult && invalidAttributes.length) {
        throw new Error('Got environment values with null or undefined: ' + invalidAttributes.join(', '))
    }

    return overrideResult
}

export const validateAttributes = (attributes) => {
    const invalidAttributes = attributes
        .filter((attribute) => {
            const isInvalidAttribute = !isValidAttribute(attribute)
            if (isInvalidAttribute) {
                console.warn('Invalid attribute', attribute)
            }
            return isInvalidAttribute
        })

    if (invalidAttributes.length) {
        throw new Error('Invalid attributes found: ' + invalidAttributes.join(', '))
    }
}

export const hashCode = (str: string): number => {
    let hash = 0

    for (let i = 0; i < str.length; i++) {
        const chr = str.charCodeAt(i)
        hash = ((hash << 5) - hash) + chr
        hash |= 0 // Convert to 32bit integer
    }

    return hash
}

export const evaluateOperation = (operator, expectedValue, actualValue) => {
    switch (operator) {
    case SupportedOperators.EQUALS:
        return actualValue === '' + expectedValue

    case SupportedOperators.NOT_EQUALS:
        return actualValue !== '' + expectedValue

    case SupportedOperators.STARTS_WITH:
        return actualValue.indexOf(expectedValue) === 0

    case SupportedOperators.CONTAINS:
        return actualValue.indexOf(expectedValue) !== -1

    default:
        console.error('ATBase: FeatureConfigService: Invalid operator', operator)
        throw new Error('Invalid operator: ' + operator)
    }
}
